import React, {
  useEffect, useRef, useState, useMemo, useCallback, Fragment, RefObject, ReactElement,
} from 'react';
import { useDispatch } from 'react-redux';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import Loader from '../../../components/loader/loader.component';
import LongClickListItem from '../../../components/long-click-list-item/long-click-list-item.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import DraggableItem from '../../../components/draggable-item/draggable-item.component';
import DragHandle from '../../../components/drag-handle/drag-handle.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import VisibleCell from '../../../components/visible-cell/visible-cell.component';
import Checkbox from '../../../components/checkbox/checkbox.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import TableSortLabel from '../../../components/table-sort-label/table-sort-label.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import ActionButton from '../../../components/action-button/action-button.component';
import HiddenBlock from '../../../components/hidden-block/hidden-block.component';
import TextBlock from '../../../components/text-block/text-block.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import ListItemCheckbox from '../../../components/list-item-checkbox/list-item-checkbox.component';
import CompletionBlock from '../../../components/completion-block/completion-block.component';
import ActionButtonContainer from '../../../components/action-button-container/action-button-container.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import ActionButtonParent from '../../../components/action-button-parent/action-button-parent.component';
import FileLink from '../../../components/file-link/file-link.component';
import TableRowNoContent from '../../../components/table-row-no-content/table-row-no-content.component';
import TableRowGroupHeader from '../../../components/table-row-group-header/table-row-group-header.component';
import TableRowGroupName from '../../../components/table-row-group-name/table-row-group-name.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import TableCellCheckbox from '../../../components/table-cell-checkbox/table-cell-checkbox.component';
import TableCellExtended from '../../../components/table-cell-extended/table-cell-extended.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import LastUpdateBlock from '../../app/common/last-update-block/last-update-block.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import DealTaskRow from './deal-task-row/deal-task-row.component';
import DealTaskRowForm from './deal-task-row-form/deal-task-row-form.component';
import DealTaskStatusIcon from '../deal-task-status-icon/deal-task-status-icon.component';
import EditIcon from '../../../icons/edit-icon';
import ExpandIcon from '../../../icons/expand-icon';
import CollapseIcon from '../../../icons/collapse-icon';
import ChangeStatusIcon from '../../../icons/change-status-icon';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import MoreIcon from '../../../icons/more-icon';
import RemoveIcon from '../../../icons/remove-icon';
import AddCommentIcon from '../../../icons/add-comment-icon';
import ArrowLinkIcon from '../../../icons/arrow-link-icon';
import ChatAssistantIcon from '../../../icons/chat-assistant-icon';
import StructureService from '../../../services/structure-service';
import SortService from '../../../services/sort-service';
import ScreenService from '../../../services/screen-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import useScroll from '../../../hooks/use-scroll';
import useArrowNavigation from '../../../hooks/use-arrow-navigation';
import useScreenSmall from '../../../hooks/use-screen-small';
import useScreenMedium from '../../../hooks/use-screen-medium';
import useMobileDevice from '../../../hooks/use-mobile-device';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import { saveDealTaskGroupOrderSettings } from './deal-tasks.action';
import {
  DEAL_TASK_ATTACHMENT_LIST_VIEW, DEAL_TASK_COMMENT_CREATE, DEAL_TASK_CREATE, DEAL_TASK_DELETE,
  DEAL_TASK_GROUP_DELETE, DEAL_TASK_EDIT, DEAL_TASK_GROUP_EDIT, DEAL_TASK_LIST_VIEW,
  DEAL_TASK_COMMENT_LIST_VIEW, DEAL_TASK_ACCESS_LEVEL_VIEW, DEAL_TASK_STATUS_EDIT, CONTACT_VIEW,
  DEAL_FILE_TEXT_DATA_VIEW,
} from '../../../constants/entity/permissions';
import {
  DATA_ATTACHMENT_ID, DATA_DEAL_TASK_GROUP_ID, DATA_DEAL_TASK_ID, DATA_IS_ANALYZABLE,
} from '../../../constants/system/data-attributes';
import { MIME_TYPE_APPLICATION_PDF } from '../../../constants/system/mime-types';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../../constants/system/sort-types';
import { ROUTE_CONTACT } from '../../../constants/system/routes';
import { DEAL_TASK_STATUS_COMPLETED, DEAL_TASK_STATUS_INITIAL } from '../../../constants/entity/deal-task-statuses';

const DealTasks: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const HEADER_NAME_ACTIONS: string = 'actions';
  const HEADER_NAME_NAME: string = 'name';
  const ID_PREFIX_CHECKBOX_EXPORT_GROUP: string = 'checkbox-export-group';

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewDeal,
    isExportMode,
    isSelectMode,
    isCustomizeMode,
    isDealTaskModalOpen,
    isLoading,
    withAllRowsSelected,
    withAllFiltersEmpty,
    dealId,
    expandedGroups,
    dealTasksData,
    dealTasksTotal,
    dealTaskNewItems,
    dealTaskActiveIds,
    dealTaskGroupActiveIds,
    dealTaskInlineSaveId,
    dealTaskGroupInlineSaveId,
    selectedRows,
    dealTaskPlainList,
    dealTaskContactPersonList,
    dealTaskCollapsedParents,
    accessLevelList,
    dealTaskStatusList,
    onLongClick,
    onOpenModal,
    onSelectRows,
    onSelectRow,
    onAddNewItem,
    onDeleteClick,
    onLoadBatchData,
    onInlineSave,
    onInlineCancel,
    onSwitchParentRow,
    onSelectAllGroupRows,
    onOpenDeleteConfirmModal,
    onOpenDeleteGroupConfirmModal,
    onOpenDealTaskListModal,
    onOpenDealTaskStatusChangeModal,
    onInlineEditModeButtonClick,
    onInlineGroupEditModeButtonClick,
    onExpansionGroupChange,
    onOpenDealFileChatModal,
    onDealTaskContactPersonSearch,
    onDealTaskContactPersonSearchClose,
    onCommentAttachmentFileViewClick,
  } = props;

  const DEFAULT_SORT: SortParams = useMemo((): SortParams => ({
    field: 'name',
    type: SORT_TYPE_ASC,
    isNumber: false,
  }), []);

  const GROUP_SORT_STATE: SortParams = useMemo((): SortParams => ({
    field: 'order',
    type: SORT_TYPE_ASC,
    isNumber: true,
  }), []);

  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isMediumScreen: boolean = useScreenMedium();
  const isMobileDevice: boolean = useMobileDevice();
  const permissionList: string[] = useCurrentUserPermissions();
  const tableRefs: RefObject<Record<string, HTMLTableElement>> = useRef({});
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);
  const itemListRef: RefObject<HTMLDivElement> | null = useRef(null);
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [fileMenuAnchor, setFileMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [groupMenuAnchor, setGroupMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [sortParams, setSortParams]: [Record<string, SortParams> | null, Function] = useState<StateValueDefault>(null);
  const [activeDealTaskGroupIndex, setActiveDealTaskGroupIndex]: [number | null, Function] = useState(null);
  const [hoverDealTaskGroupIndex, setHoverDealTaskGroupIndex]: [number | null, Function] = useState(null);

  const commonDealTasksTotal: number = useMemo((): number => (
    dealTasksData && dealTasksData.length
      ? dealTasksData.reduce((dealTaskItemsTotal: number, dealTaskGroupItem: DealTaskGroup): number => (
        dealTaskItemsTotal + dealTaskGroupItem.tasks.length
      ), 0) : 0
  ), [dealTasksData]);
  const scrollDeps: DepsItem[] = useMemo((): DepsItem[] => [
    dealTasksTotal, commonDealTasksTotal, isLoading, isEditMode,
  ], [dealTasksTotal, commonDealTasksTotal, isLoading, isEditMode]);

  const dealTaskAttachmentListViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_ATTACHMENT_LIST_VIEW], []);
  const dealTaskCommentListViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_COMMENT_LIST_VIEW], []);
  const dealTaskEditPermissions: string[] = useMemo((): string[] => [DEAL_TASK_EDIT], []);
  const dealTaskGroupEditPermissions: string[] = useMemo((): string[] => [DEAL_TASK_GROUP_EDIT], []);
  const dealTaskGroupDeletePermissions: string[] = useMemo((): string[] => [DEAL_TASK_GROUP_DELETE], []);
  const dealTaskListViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_LIST_VIEW], []);
  const dealTaskCreatePermissions: string[] = useMemo((): string[] => [DEAL_TASK_CREATE], []);
  const attachmentActionMenuPermissions: string[] = useMemo((): string[] => [DEAL_FILE_TEXT_DATA_VIEW], []);

  const formPrefix: string = 'dealData.dealTasksData';
  const withFilters: boolean = !withAllFiltersEmpty && !isEditMode && !isExportMode && !isSelectMode
    && !isCustomizeMode;
  const withButtons: boolean = isEditMode || isExportMode;
  const isDealTaskGroupEditAllowed: boolean = StructureService.checkPermission(DEAL_TASK_GROUP_EDIT, permissionList);
  const isGroupOrderAllowed: boolean = isCustomizeMode && expandedGroups && !expandedGroups.length
    && !isMobileDevice && withAllFiltersEmpty && isDealTaskGroupEditAllowed;
  const dealTaskGroupIds: number[] = dealTasksData && dealTasksData.length
    ? dealTasksData.map((dealTaskGroupItem: DealTaskGroup): number => dealTaskGroupItem.id) : [];

  let commonRowIndex: number = 0;
  let isTableLoading: boolean = false;

  const fileActionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <ChatAssistantIcon />,
      text: 'Open File Chat',
      permission: DEAL_FILE_TEXT_DATA_VIEW,
      isVisibleByEntityTypeValue: FormatService.BOOLEAN_VALUE_TRUE,
      onClick: (
        event: React.MouseEvent<Element, MouseEvent>, attachmentId: number,
      ): void => {
        let dealTaskId: number | null = null;
        let dealTaskAttachment: Attachment | null = null;
        dealTasksData.forEach((dealTaskGroupData: DealTaskGroup): void => {
          if (dealTaskGroupData.tasks && dealTaskGroupData.tasks.length) {
            dealTaskGroupData.tasks.forEach((dealTaskData: DealTask): void => {
              if (dealTaskData.attachments && dealTaskData.attachments.length) {
                const dealTaskAttachmentItem: Attachment | null = dealTaskData.attachments.find((
                  attachmentItem: Attachment,
                ): boolean => (
                  attachmentItem.id === attachmentId
                )) || null;

                if (dealTaskAttachmentItem) {
                  dealTaskId = dealTaskData.id;
                  dealTaskAttachment = dealTaskAttachmentItem;
                }
              }
            });
          }
        });

        if (onOpenDealFileChatModal && dealTaskAttachment) {
          onOpenDealFileChatModal(event, dealTaskAttachment, dealTaskId);
        }
      },
    },
  ], [dealTasksData]);

  const sortTableRows: Function = useCallback((
    dealTaskGroupId: number, columnName: string, isNumber?: boolean,
  ): void => {
    setSortParams({
      ...(sortParams || {}),
      [dealTaskGroupId]: {
        field: columnName,
        type: !!sortParams && !!sortParams[dealTaskGroupId] && sortParams[dealTaskGroupId].field === columnName
        && sortParams[dealTaskGroupId].type === SORT_TYPE_DESC
          ? SORT_TYPE_ASC : SORT_TYPE_DESC,
        isNumber,
      },
    });
  }, [sortParams]);

  const addNewItem: Function = useCallback((dealTaskGroupId: number): void => {
    if (onAddNewItem) {
      onAddNewItem(dealTaskGroupId, {
        id: StructureService.getNewItemId(dealTaskNewItems[dealTaskGroupId] || []),
        groupId: dealTaskGroupId,
        parentId: null,
        name: '',
        dueDate: '',
        contactId: '',
        status: DEAL_TASK_STATUS_INITIAL,
      });
    }
  }, [dealTasksData, dealTaskNewItems]);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const onGroupFieldClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const checkIsDealTaskActive: Function = useCallback((dealTaskId: number): boolean => (
    dealTaskActiveIds ? dealTaskActiveIds.includes(dealTaskId) : false
  ), [dealTaskActiveIds]);

  const openModal: Function = useCallback((
    dealTaskItem: DealTask,
    dealTaskGroupItem: DealTaskGroup,
    isCommentActive?: boolean,
  ): void => {
    if (onOpenModal) {
      onOpenModal(dealTaskItem, dealTaskGroupItem, isCommentActive);
    }
  }, []);

  const openModalById: Function = useCallback((dealTaskId: number, isCommentActive?: boolean): void => {
    if (dealTasksData) {
      dealTasksData.forEach((dealTaskGroupItem: DealTaskGroup): void => {
        dealTaskGroupItem.tasks.forEach((dealTaskItem: DealTask): void => {
          if (dealTaskItem.id === dealTaskId && !isDealTaskModalOpen) {
            openModal(dealTaskItem, dealTaskGroupItem, isCommentActive);
          }

          if (dealTaskItem.subTasks && dealTaskItem.subTasks.length) {
            dealTaskItem.subTasks.forEach((dealSubTaskItem: DealTask): void => {
              if (dealSubTaskItem.id === dealTaskId && !isDealTaskModalOpen) {
                openModal(dealSubTaskItem, dealTaskGroupItem, isCommentActive);
              }
            });
          }
        });
      });
    }
  }, [isDealTaskModalOpen, dealTasksData]);

  const onCommentAttachmentFileOpenActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setFileMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const onCheckIsFileMenuItemVisible: Function = useCallback((
    actionMenuItem: ActionMenuItem, isAnalyzable: string | null,
  ): boolean => (
    ((actionMenuItem.isVisibleByEntityTypeValue && isAnalyzable && FormatService.extractBooleanValue(isAnalyzable))
      || !actionMenuItem.isVisibleByEntityTypeValue)
  ), []);

  const closeFileActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setFileMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const checkIsFileAnalyzable: Function = useCallback((file?: FileItem): boolean => {
    return !!file && !!file.mimeType && file.mimeType === MIME_TYPE_APPLICATION_PDF;
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <AddCommentIcon />,
      text: 'Add Comment',
      permission: DEAL_TASK_COMMENT_CREATE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTaskId: number): void => {
        openModalById(dealTaskId, true);
      },
    },
    {
      id: 2,
      icon: <EditIcon />,
      text: 'Update Task',
      permission: DEAL_TASK_EDIT,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTaskId: number): void => {
        onOpenDealTaskListModal(event, [dealTaskId]);
      },
    },
    {
      id: 3,
      icon: <ChangeStatusIcon />,
      text: 'Change Status',
      permission: DEAL_TASK_STATUS_EDIT,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTaskId: number): void => {
        onOpenDealTaskStatusChangeModal(event, [dealTaskId]);
      },
    },
    {
      id: 4,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_TASK_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTaskId: number): void => {
        if (dealTaskId) {
          onOpenDeleteConfirmModal(event, dealTaskId);
        }
      },
    },
  ], [isDealTaskModalOpen, dealTasksData]);

  const actionGroupMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <EditIcon />,
      text: 'Edit',
      permission: DEAL_TASK_GROUP_EDIT,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTaskGroupId: number): void => {
        if (dealTaskGroupId) {
          onInlineGroupEditModeButtonClick(event, dealTaskGroupId);
        }
      },
    },
    {
      id: 2,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_TASK_GROUP_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTaskGroupId: number): void => {
        if (dealTaskGroupId) {
          onOpenDeleteGroupConfirmModal(dealTaskGroupId);
        }
      },
    },
  ], [isEditMode, dealTaskGroupActiveIds]);

  const actionMenuPermissions: string[] = useMemo((): string[] => (
    actionMenuItems && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | null => (
      actionMenuItem.permission || null
    )).filter((permissionItem: string | null): boolean => !!permissionItem) as string[]
  ), [actionMenuItems]);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const openGroupActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setGroupMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const closeGroupActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setGroupMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const deleteClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealTaskId: number, dealTaskGroupId: number,
  ): void => {
    if (onDeleteClick) {
      onDeleteClick(dealTaskId, dealTaskGroupId);
    }

    event.stopPropagation();
  }, [dealTaskNewItems]);

  const switchParentRow: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealTaskId: number,
  ): void => {
    if (onSwitchParentRow) {
      onSwitchParentRow(dealTaskId);
    }

    event.stopPropagation();
  }, [dealTaskCollapsedParents]);

  const checkIsRowSelected: Function = useCallback((
    rowGroupId: number, rowId: number, selectedRows: StructureItem[],
  ): boolean => (
    selectedRows[rowGroupId] ? selectedRows[rowGroupId].includes(rowId) : false
  ), []);

  const onRowClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealTask: DealTask, dealTaskGroup: DealTaskGroup,
    isListView?: boolean,
  ): void => {
    if (!isCustomizeMode) {
      if (isExportMode || isSelectMode) {
        if (onSelectRow) {
          onSelectRow(event, dealTaskGroup.id, dealTask.id, selectedRows, onSelectRows);
        }
      } else if ((!isEditMode && !isNewDeal) || isListView) {
        const withNoSelection: boolean = ScreenService.checkNoSelection();

        if (withNoSelection) {
          openModal(dealTask, dealTaskGroup);
        }
      }
    }
  }, [isExportMode, isSelectMode, isCustomizeMode, isEditMode, isNewDeal, selectedRows]);

  const tableDealTaskListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      title: 'Name',
      isSortable: true,
      isNumber: false,
      isSticky: true,
      component: (dealTask: DealTask, isSubTask?: boolean): ReactElement => (
        <TableCellExtended
          isText
          isSticky={!isExportMode && !isSelectMode && !isCustomizeMode}
          isStickyLeft={!isExportMode && !isSelectMode && !isCustomizeMode}
          isStickyLeftSelectExtended={isExportMode || isSelectMode || isCustomizeMode}
        >
          <FlexContainer>
            <ActionButtonParent
              isParent={!isEditMode && !isNewDeal && !isSubTask && dealTaskCollapsedParents
                && (dealTaskCollapsedParents[dealTask.id] || dealTaskCollapsedParents[dealTask.id] === false)
                && dealTask.subTasks && !!dealTask.subTasks.length}
              isOpen={!!dealTaskCollapsedParents[dealTask.id]}
              withOffset={isSubTask}
              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                switchParentRow(event, dealTask.id);
              }}
            />
            <DealTaskStatusIcon withRightOffset withLeftOffset={isSubTask} dealTaskData={dealTask} />
            <div>
              {dealTask.name}
            </div>
          </FlexContainer>
        </TableCellExtended>
      ),
    },
    {
      id: 2,
      name: 'parentId',
      title: 'Parent Task',
      isSortable: false,
      isNumber: false,
      isEditModeOnly: true,
      component: (dealTask: DealTask, isSubTask?: boolean, dealTaskParent?: DealTask): ReactElement => (
        <TableCell isText>
          {isSubTask && dealTaskParent && dealTaskParent.name}
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'statusText',
      title: 'Status',
      isSortable: true,
      isNumber: false,
      component: (dealTask: DealTask): ReactElement => (
        <TableCell>
          {dealTask.statusText}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'dueDate',
      title: 'Due Date',
      isSortable: true,
      isNumber: false,
      component: (dealTask: DealTask): ReactElement => {
        const parsedDueDate: Date | null = FormatService.formatParsedDate(dealTask.dueDate);

        return (
          <TableCell>
            {parsedDueDate && FormatService.formatDateString(parsedDueDate)}
          </TableCell>
        );
      },
    },
    {
      id: 5,
      name: 'contact.fullName',
      title: 'Assigned To',
      isSortable: true,
      isNumber: false,
      component: (dealTask: DealTask): ReactElement => (
        <TableCell>
          {
            dealTask.contact && (
              <FilteredLink
                withNewWindow
                link={!isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode
                  && `${ROUTE_CONTACT}/${dealTask.contact.id}`}
                permission={CONTACT_VIEW}
              >
                {dealTask.contact.fullName}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'createdDate',
      title: 'Date Created',
      isSortable: true,
      isNumber: false,
      component: (dealTask: DealTask): ReactElement => (
        <TableCell>
          {dealTask.createdDate}
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'documents',
      title: 'Documents',
      isSortable: false,
      isNumber: false,
      permission: DEAL_TASK_ATTACHMENT_LIST_VIEW,
      component: (
        dealTask: DealTask, isSubTask?: boolean, dealTaskParent?: DealTask, dealTaskGroup?: DealTaskGroup,
      ): ReactElement => (
        <TableCell isText>
          {
            !isEditMode && dealTask.attachments && !!dealTask.attachments.length && (
              <CheckAccess permissions={dealTaskAttachmentListViewPermissions}>
                <div>
                  {
                    dealTask.attachments.map((attachmentItem: Attachment): ReactElement => (
                      <FlexContainer isInline key={`${dealTask.id}-${attachmentItem.id}`}>
                        <FileLink
                          isDisabled={isSelectMode || isExportMode || isCustomizeMode}
                          withActionMenu={!isEditMode && !isNewDeal}
                          fileLink={attachmentItem.file}
                          actionProps={{
                            [DATA_ATTACHMENT_ID]: attachmentItem.id,
                            [DATA_IS_ANALYZABLE]: checkIsFileAnalyzable(attachmentItem.file)
                              ? FormatService.BOOLEAN_VALUE_TRUE : null,
                          }}
                          actionMenuItems={fileActionMenuItems}
                          actionMenuPermissions={attachmentActionMenuPermissions}
                          entityTypeProp={DATA_IS_ANALYZABLE}
                          onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onCommentAttachmentFileViewClick(
                              event,
                              attachmentItem.file,
                              dealTask.name,
                              dealTaskGroup ? dealTaskGroup.name : null,
                            );
                          }}
                          onOpenActionMenu={onCommentAttachmentFileOpenActionMenu}
                          onCheckIsMenuItemVisible={onCheckIsFileMenuItemVisible}
                        />
                      </FlexContainer>
                    ))
                  }
                </div>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'updates',
      title: 'Last Update',
      isSortable: false,
      isNumber: false,
      permission: DEAL_TASK_ATTACHMENT_LIST_VIEW,
      component: (dealTask: DealTask): ReactElement => (
        <TableCell isText isTextNormal isExtended>
          <LastUpdateBlock
            withNoUpdates
            isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
            commentList={dealTask.comments}
            attachmentList={dealTask.attachments}
            commentListViewPermissions={dealTaskCommentListViewPermissions}
            attachmentListViewPermissions={dealTaskAttachmentListViewPermissions}
          />
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'accessLevel.title',
      title: 'Access Limit',
      permission: DEAL_TASK_ACCESS_LEVEL_VIEW,
      isSortable: false,
      isNumber: false,
      isEditModeOnly: true,
      component: (): ReactElement => <TableCell />,
    },
    {
      id: 10,
      name: 'actions',
      title: 'Actions',
      isSortable: false,
      isNumber: false,
      isHeaderNameHidden: true,
      isSticky: true,
      isStickyRight: true,
      component: (dealTask: DealTask): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight onClick={onActionCellClick}>
          {
            !isExportMode && !isSelectMode && !isCustomizeMode
            && (!isEditMode || !checkIsDealTaskActive(dealTask.id)) && (
              <CheckAccess permissions={dealTaskEditPermissions}>
                <ActionButton
                  withNoPadding
                  withFixedSize
                  isHidden={!!isSaving}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onInlineEditModeButtonClick(event, dealTask.id);
                  }}
                >
                  <EditIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
          {
            !isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && (
              <CheckAccess permissions={actionMenuPermissions}>
                <ActionButton
                  withPopup
                  withNoPadding
                  withFixedSize
                  isHidden={!!isSaving}
                  onClick={openActionMenu}
                  {...{
                    [DATA_DEAL_TASK_ID]: dealTask.id,
                  }}
                >
                  <MoreIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
    },
  ], [isEditMode, isExportMode, isSelectMode, isCustomizeMode, dealTaskCollapsedParents, dealTaskActiveIds, isSaving]);

  const visibleTableFieldsTotal: number = tableDealTaskListFields.filter((tableField: TableField): boolean => (
    ((tableField.isEditModeOnly && (isEditMode || isNewDeal)) || !tableField.isEditModeOnly)
    && ((!!tableField.permission && StructureService.checkPermission(tableField.permission, permissionList))
      || !tableField.permission)
  )).length;

  const longClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealTaskGroupId: number, dealTaskId: number,
  ): void => {
    if (!isEditMode && !isSelectMode && !isExportMode) {
      if (onLongClick) {
        onLongClick();
      }

      onSelectRow(event, dealTaskGroupId, dealTaskId, selectedRows, onSelectRows);
    }
  }, [selectedRows, isEditMode, isSelectMode, isExportMode, isCustomizeMode]);

  const onDropGroupItem: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    const newDealTaskGroupIds: number[] = [...dealTaskGroupIds];
    const activeDealTaskGroupId: number = newDealTaskGroupIds[dragIndex];
    StructureService.deleteListItemByIndex(newDealTaskGroupIds, dragIndex);
    StructureService.insertListItemByIndex(newDealTaskGroupIds, hoverIndex, activeDealTaskGroupId);

    dispatch(saveDealTaskGroupOrderSettings({
      dealId,
      dealTaskGroupIds: newDealTaskGroupIds.filter((dealTaskGroupId: number): boolean => !!dealTaskGroupId),
    }));
  }, [dealTaskGroupIds, dealId]);

  const onHoverGroupItem: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    setActiveDealTaskGroupIndex(dragIndex);
    setHoverDealTaskGroupIndex(hoverIndex);
  }, []);

  const onReleaseGroupItem: Function = useCallback((): void => {
    setActiveDealTaskGroupIndex(null);
    setHoverDealTaskGroupIndex(null);
  }, []);

  const onScroll: MouseScrollHandlerFunction = useCallback((): void => {
    if (containerRef && containerRef.current) {
      ScreenService.infiniteScroll(
        isSmallScreen ? containerRef.current.offsetTop : containerRef.current.scrollTop,
        containerRef.current.clientHeight,
        itemListRef && itemListRef.current ? itemListRef.current.clientHeight : 0,
        (): void => {
          if (onLoadBatchData && dealTasksTotal > commonDealTasksTotal && !isLoading) {
            onLoadBatchData();
          }
        },
        isSmallScreen,
      );
    }
  }, [containerRef, itemListRef, isSmallScreen, dealTasksTotal, commonDealTasksTotal, isLoading]);

  useEffect((): void => {
    if (dealTasksData) {
      const initialSortParams: Record<string, SortParams> = sortParams || {};
      dealTasksData.forEach((dealTaskGroup: DealTaskGroup): void => {
        if (!initialSortParams[dealTaskGroup.id]) {
          initialSortParams[dealTaskGroup.id] = DEFAULT_SORT;
        }
      });
      setSortParams(initialSortParams);
    }
  }, [dealTasksData]);

  useScroll(onScroll, scrollDeps);

  useArrowNavigation(inputRefs);

  return (
    <CheckAccess permissions={dealTaskEditPermissions} isEnabled={isEditMode || isNewDeal}>
      {
        dealTasksData && !!dealTasksData.length ? (
          <Card>
            <ContentListContainerOffset
              withTabs
              isDefault={isSmallScreen}
              withFilters={withFilters}
              withButtons={withButtons}
              ref={containerRef}
              onScroll={!isSmallScreen ? onScroll : undefined}
            >
              <div ref={itemListRef}>
                {
                  SortService.sort(dealTasksData, GROUP_SORT_STATE).map((
                    dealTaskGroup: DealTaskGroup, dealTaskGroupIndex: number,
                  ): ReactElement | boolean => {
                    const isGroupCollapsed: boolean = expandedGroups
                      && !expandedGroups.includes(dealTaskGroupIndex + 1);
                    const dealTaskGroupTasksTotal: number = (
                      dealTaskGroup && dealTaskGroup.tasks && dealTaskGroup.tasks.length
                        ? dealTaskGroup.tasks.reduce((tasksTotal: number, dealTaskItem: DealTask): number => (
                          tasksTotal + ((dealTaskItem.subTasks && dealTaskItem.subTasks.length) || 0) + 1
                        ), 0) : 0
                    );
                    const dealTaskGroupTasksSelectedTotal: number = selectedRows && selectedRows[dealTaskGroup.id]
                      ? selectedRows[dealTaskGroup.id].length : 0;
                    const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroup.id);
                    const withGroupItems: boolean = dealTaskGroup.tasks && !!dealTaskGroup.tasks.length;
                    const isGroupLoading: boolean = !!dealTaskGroup.tasksTotal
                      && dealTaskGroup.tasksTotal > dealTaskGroupTasksTotal;
                    const isDealTaskGroupSelected: boolean = withAllRowsSelected
                      || dealTaskGroupTasksSelectedTotal === dealTaskGroupTasksTotal;
                    const isDealTaskGroupIndeterminate: boolean = !withAllRowsSelected
                      && dealTaskGroupTasksSelectedTotal > 0
                      && dealTaskGroupTasksSelectedTotal < dealTaskGroupTasksTotal;
                    const isActiveGroup: boolean = dealTaskGroupActiveIds
                      && dealTaskGroupActiveIds.includes(dealTaskGroup.id);
                    const isCurrentTableLoading: boolean = isTableLoading;
                    const isGroupRendered: boolean = ((!!dealTaskGroup.tasksTotal && !!dealTaskGroupTasksTotal)
                      || !dealTaskGroup.tasksTotal) && !isCurrentTableLoading;
                    const isVisible: boolean = (!isExportMode && !isSelectMode) || ((isExportMode || isSelectMode)
                      && (withGroupItems || isGroupLoading));

                    isTableLoading = isTableLoading || isGroupLoading;

                    return (isGroupRendered || isGroupCollapsed) && (
                      <DraggableItem
                        withHighlight
                        isDisabled={!isGroupOrderAllowed}
                        isHovered={hoverDealTaskGroupIndex === dealTaskGroupIndex}
                        isHoverInverted={hoverDealTaskGroupIndex && activeDealTaskGroupIndex
                          && hoverDealTaskGroupIndex > activeDealTaskGroupIndex}
                        key={dealTaskGroup.id}
                        itemId={dealTaskGroup.id}
                        index={dealTaskGroupIndex}
                        onDropItem={onDropGroupItem}
                        onHoverItem={onHoverGroupItem}
                        onReleaseItem={onReleaseGroupItem}
                      >
                        <ExpansionGroup
                          isTableGroup
                          isExpanded={(isSmallScreen && !isGroupCollapsed) || !isSmallScreen}
                          withNoHeader={!isSmallScreen}
                          key={dealTaskGroup.id}
                          onChange={(event: React.SyntheticEvent): void => {
                            onExpansionGroupChange(event, dealTaskGroupIndex + 1, !isGroupRendered);
                          }}
                          headerComponent={(
                            isSmallScreen && (
                              <FlexContainer>
                                {
                                  (!isEditMode && !isNewDeal) || !isActiveGroup ? (
                                    <FlexContainer isSpread isStart>
                                      {dealTaskGroup.name}
                                    </FlexContainer>
                                  ) : (
                                    <Fragment>
                                      <CheckAccess permissions={dealTaskGroupEditPermissions}>
                                        <FormFieldText isHidden name={`${formPrefix}[${formKeyDealTaskGroup}].id`} />
                                        <FormFieldText
                                          isLight
                                          withUnderline
                                          name={`${formPrefix}[${formKeyDealTaskGroup}].name`}
                                          placeholder="Group Name"
                                          onClick={onGroupFieldClick}
                                        />
                                      </CheckAccess>
                                      {
                                        ((!isEditMode && !isNewDeal) || !isActiveGroup) && (
                                          <ActionButtonContainer>
                                            {
                                              !isActiveGroup && (
                                                <CheckAccess permissions={dealTaskGroupEditPermissions}>
                                                  <ActionButton
                                                    isLight
                                                    withSmallPadding
                                                    isHidden={!!isSaving}
                                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                      onInlineGroupEditModeButtonClick(event, dealTaskGroup.id);
                                                    }}
                                                  >
                                                    <EditIcon />
                                                  </ActionButton>
                                                </CheckAccess>
                                              )
                                            }
                                            {
                                              !isExportMode && !isSelectMode && !isEditMode
                                              && !isNewDeal && !isSmallScreen && (
                                                <CheckAccess permissions={dealTaskGroupDeletePermissions}>
                                                  <ActionButton
                                                    isLight
                                                    isActive
                                                    withPopup
                                                    withSmallPadding
                                                    onClick={openGroupActionMenu}
                                                    {...{
                                                      [DATA_DEAL_TASK_GROUP_ID]: dealTaskGroup.id,
                                                    }}
                                                  >
                                                    <MoreIcon />
                                                  </ActionButton>
                                                </CheckAccess>
                                              )
                                            }
                                          </ActionButtonContainer>
                                        )
                                      }
                                    </Fragment>
                                  )
                                }
                              </FlexContainer>
                            )
                          )}
                        >
                          <CheckAccess permissions={dealTaskListViewPermissions}>
                            {
                              !isSmallScreen ? (
                                <HiddenBlock isActive={!isVisible}>
                                  <Table
                                    withStickyHeader
                                    minWidth={isEditMode ? 3800 : 2200}
                                    isFullWidth={isEditMode || isNewDeal}
                                    ref={(ref: HTMLTableElement): void => {
                                      if (tableRefs.current) {
                                        tableRefs.current[dealTaskGroup.id] = ref;
                                      }
                                    }}
                                  >
                                    <TableHead isSubHeader withFixedCellsSize>
                                      {
                                        !isSmallScreen && (
                                          (!isEditMode && !isNewDeal) || !isActiveGroup ? (
                                            <TableRowGroupName
                                              isButton
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                onExpansionGroupChange(event, dealTaskGroupIndex + 1, !isGroupRendered);
                                              }}
                                            >
                                              <TableCell
                                                isText
                                                isTextWrap
                                                isSticky
                                                isStickyLeft
                                                isStickyGroupLeft
                                                width="25%"
                                                colSpan={isExportMode || isSelectMode ? 2 : 1}
                                              >
                                                <FlexContainer isSpread>
                                                  {
                                                    isGroupOrderAllowed && (
                                                      <DragHandle />
                                                    )
                                                  }
                                                  <FlexContainer isSpread isStart>
                                                    {dealTaskGroup.name}
                                                  </FlexContainer>
                                                </FlexContainer>
                                              </TableCell>
                                              <TableCell>
                                                {
                                                  !!dealTaskGroup.tasksTotal && (
                                                    <CompletionBlock
                                                      withPostfix
                                                      withNoProgressBar={isMediumScreen}
                                                      itemsTotal={dealTaskGroup.tasksTotal}
                                                      completedItemsTotal={dealTaskGroup.tasksCompletedTotal}
                                                    />
                                                  )
                                                }
                                              </TableCell>
                                              <TableCell colSpan={visibleTableFieldsTotal - 3} />
                                              <TableCell
                                                isActions
                                                isTextRight
                                                isSticky
                                                isStickyRight
                                                isStickyGroupRight
                                              >
                                                <ActionButtonContainer>
                                                  {
                                                    !isExportMode && !isSelectMode && !isEditMode && !isSmallScreen && (
                                                      <CheckAccess permissions={dealTaskGroupDeletePermissions}>
                                                        <ActionButton
                                                          isLight
                                                          isActive
                                                          withPopup
                                                          withNoPadding
                                                          onClick={openGroupActionMenu}
                                                          {...{
                                                            [DATA_DEAL_TASK_GROUP_ID]: dealTaskGroup.id,
                                                          }}
                                                        >
                                                          <MoreIcon />
                                                        </ActionButton>
                                                      </CheckAccess>
                                                    )
                                                  }
                                                  {
                                                    isEditMode && !isActiveGroup && (
                                                      <CheckAccess permissions={dealTaskGroupEditPermissions}>
                                                        <ActionButton
                                                          isLight
                                                          isHidden={!!isSaving}
                                                          withNoPadding
                                                          onClick={(
                                                            event: React.MouseEvent<Element, MouseEvent>,
                                                          ): void => {
                                                            onInlineGroupEditModeButtonClick(event, dealTaskGroup.id);
                                                          }}
                                                        >
                                                          <EditIcon />
                                                        </ActionButton>
                                                      </CheckAccess>
                                                    )
                                                  }
                                                  {
                                                    !isSmallScreen && (
                                                      <ActionButton
                                                        isLight
                                                        isDisabled={!isGroupRendered}
                                                        withNoPadding
                                                        onClick={(
                                                          event: React.MouseEvent<Element, MouseEvent>,
                                                        ): void => {
                                                          onExpansionGroupChange(
                                                            event,
                                                            dealTaskGroupIndex + 1,
                                                            !isGroupRendered,
                                                          );
                                                        }}
                                                      >
                                                        {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                      </ActionButton>
                                                    )
                                                  }
                                                </ActionButtonContainer>
                                              </TableCell>
                                            </TableRowGroupName>
                                          ) : (
                                            <TableRowGroupName
                                              isButton
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                onExpansionGroupChange(
                                                  event,
                                                  dealTaskGroupIndex + 1,
                                                  !isGroupRendered,
                                                );
                                              }}
                                            >
                                              <CheckAccess permissions={dealTaskGroupEditPermissions}>
                                                <TableCell isSticky isStickyLeft isStickyGroupLeft width="25%">
                                                  <FlexContainer isSpread>
                                                    <FormFieldText
                                                      isHidden
                                                      name={`${formPrefix}[${formKeyDealTaskGroup}].id`}
                                                    />
                                                    <FormFieldText
                                                      isLight
                                                      withUnderline
                                                      name={`${formPrefix}[${formKeyDealTaskGroup}].name`}
                                                      placeholder="Group Name"
                                                      onClick={onGroupFieldClick}
                                                    />
                                                  </FlexContainer>
                                                </TableCell>
                                                <TableCell colSpan={visibleTableFieldsTotal - 2} />
                                                <TableCell
                                                  isActions
                                                  isTextRight
                                                  isSticky
                                                  isStickyRight
                                                  isStickyGroupRight
                                                >
                                                  <ActionButtonContainer>
                                                    {
                                                      !isSmallScreen && (
                                                        <ActionButton
                                                          isLight
                                                          withNoPadding
                                                          isDisabled={!isGroupRendered}
                                                          onClick={(
                                                            event: React.MouseEvent<Element, MouseEvent>,
                                                          ): void => {
                                                            onExpansionGroupChange(
                                                              event,
                                                              dealTaskGroupIndex + 1,
                                                              !isGroupRendered,
                                                            );
                                                          }}
                                                        >
                                                          {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                        </ActionButton>
                                                      )
                                                    }
                                                  </ActionButtonContainer>
                                                </TableCell>
                                              </CheckAccess>
                                            </TableRowGroupName>
                                          )
                                        )
                                      }
                                      <TableRowGroupHeader
                                        isSticky
                                        isEdit={isEditMode && isActiveGroup}
                                        isHidden={!isSmallScreen && isGroupCollapsed}
                                      >
                                        {
                                          (isExportMode || isSelectMode) && (
                                            <TableCellCheckbox isSticky isStickyLeft>
                                              {
                                                dealTaskGroup.tasks && !!dealTaskGroup.tasks.length && (
                                                  <Checkbox
                                                    isHeader
                                                    isChecked={isDealTaskGroupSelected}
                                                    isIndeterminate={isDealTaskGroupIndeterminate}
                                                    id={`${ID_PREFIX_CHECKBOX_EXPORT_GROUP}-${dealTaskGroup.id}`}
                                                    onChange={(event: React.ChangeEvent): void => {
                                                      onSelectAllGroupRows(
                                                        event,
                                                        dealTaskGroup.id,
                                                        selectedRows,
                                                        onSelectRows,
                                                        dealTasksData,
                                                      );
                                                    }}
                                                  />
                                                )
                                              }
                                            </TableCellCheckbox>
                                          )
                                        }
                                        {
                                          tableDealTaskListFields.map((tableField: TableField): ReactElement => (
                                            <VisibleCell
                                              isEditMode={isEditMode || isNewDeal}
                                              key={tableField.id}
                                              tableField={tableField}
                                              permissionList={permissionList}
                                            >
                                              <TableCell
                                                isActions={tableField.name === HEADER_NAME_ACTIONS}
                                                isText={tableField.name === HEADER_NAME_NAME}
                                                isTextRight={tableField.name === HEADER_NAME_ACTIONS}
                                                isSticky={!isCustomizeMode && !isExportMode && !isSelectMode
                                                  && tableField.isSticky}
                                                isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode
                                                  && tableField.isSticky && !tableField.isStickyRight}
                                                isStickyLeftSelectExtended={!isCustomizeMode
                                                  && (isExportMode || isSelectMode) && tableField.isSticky
                                                  && !tableField.isStickyRight}
                                                isStickyRight={!isCustomizeMode && !isExportMode && !isSelectMode
                                                  && tableField.isSticky && tableField.isStickyRight}
                                                isStickyRightHeader={!isCustomizeMode && !isExportMode && !isSelectMode
                                                  && tableField.isSticky && tableField.isStickyRight}
                                                sortDirection={(
                                                  sortParams && sortParams[dealTaskGroup.id] && tableField.isSortable
                                                  && sortParams[dealTaskGroup.id].field === tableField.name
                                                    ? sortParams[dealTaskGroup.id].type : false
                                                )}
                                              >
                                                <CheckAccess
                                                  permissions={tableField.permission ? [tableField.permission] : []}
                                                >
                                                  {
                                                    tableField.isSortable && !isEditMode && !isNewDeal ? (
                                                      <TableSortLabel
                                                        withNoLabel={tableField.isHeaderNameHidden}
                                                        isActive={sortParams && sortParams[dealTaskGroup.id]
                                                          ? sortParams[dealTaskGroup.id].field === tableField.name
                                                          : false}
                                                        sortType={sortParams && sortParams[dealTaskGroup.id]
                                                          ? sortParams[dealTaskGroup.id].type : SORT_TYPE_ASC}
                                                        onClick={(): void => {
                                                          sortTableRows(
                                                            dealTaskGroup.id,
                                                            tableField.name,
                                                            tableField.isNumber,
                                                          );
                                                        }}
                                                      >
                                                        {tableField.title}
                                                      </TableSortLabel>
                                                    ) : (
                                                      !tableField.isHeaderNameHidden && (
                                                        <TextBlock>
                                                          {tableField.title}
                                                        </TextBlock>
                                                      )
                                                    )
                                                  }
                                                </CheckAccess>
                                              </TableCell>
                                            </VisibleCell>
                                          ))
                                        }
                                      </TableRowGroupHeader>
                                    </TableHead>
                                    <TableBody
                                      isPlain
                                      withZebraRows
                                      withBorderedCells
                                      withFixedCellsSize
                                      isHidden={!isSmallScreen && isGroupCollapsed}
                                      withFixedCellsSizeExtended={isEditMode}
                                    >
                                      {
                                        dealTaskGroup.tasks && !!dealTaskGroup.tasks.length
                                        && sortParams && sortParams[dealTaskGroup.id] && dealTaskCollapsedParents
                                        && SortService.sort(dealTaskGroup.tasks, sortParams[dealTaskGroup.id])
                                          .map((dealTask: DealTask): ReactElement => (
                                            <Fragment key={dealTask.id}>
                                              {
                                                [
                                                  dealTask,
                                                  ...(dealTask.subTasks && !!dealTask.subTasks.length
                                                    ? SortService.sort(dealTask.subTasks, sortParams[dealTaskGroup.id])
                                                    : []),
                                                ].map((dealSubTaskItem: DealTask): ReactElement | boolean => {
                                                  const isSelected: boolean = selectedRows ? withAllRowsSelected
                                                    || checkIsRowSelected(
                                                      dealTaskGroup.id, dealSubTaskItem.id, selectedRows,
                                                    ) : false;
                                                  const isSubTask: boolean = dealSubTaskItem.id !== dealTask.id;
                                                  const isActive: boolean = checkIsDealTaskActive(dealSubTaskItem.id);
                                                  const isInlineSaving: boolean = dealTaskInlineSaveId !== null
                                                  && dealTaskGroupInlineSaveId !== null
                                                    ? dealTask.id === dealTaskInlineSaveId
                                                    && dealTaskGroup.id === dealTaskGroupInlineSaveId : isSaving;
                                                  const commonDealTaskIndex: number = ++commonRowIndex;

                                                  return ((isSubTask && dealSubTaskItem.parentId
                                                      && dealTaskCollapsedParents[dealSubTaskItem.parentId])
                                                    || !isSubTask) && (
                                                    <DealTaskRow
                                                      isActive={isActive}
                                                      isSaving={isSaving}
                                                      isInlineSaving={isInlineSaving}
                                                      isSaveDisabled={isSaveDisabled}
                                                      isEditMode={isEditMode}
                                                      isNewDeal={isNewDeal}
                                                      isExportMode={isExportMode}
                                                      isSelectMode={isSelectMode}
                                                      isCustomizeMode={isCustomizeMode}
                                                      isSubTask={isSubTask}
                                                      isSelected={isSelected}
                                                      key={dealSubTaskItem.id}
                                                      inputRefs={inputRefs}
                                                      tableDealTaskListFields={tableDealTaskListFields}
                                                      dealTaskIndex={commonDealTaskIndex}
                                                      dealTaskItem={dealSubTaskItem}
                                                      dealTaskParent={isSubTask ? dealTask : null}
                                                      dealTaskGroup={dealTaskGroup}
                                                      dealTaskPlainList={dealTaskPlainList}
                                                      dealTaskStatusList={dealTaskStatusList}
                                                      dealTaskContactPersonList={dealTaskContactPersonList}
                                                      accessLevelList={accessLevelList}
                                                      permissionList={permissionList}
                                                      onRowClick={onRowClick}
                                                      onLongClick={longClick}
                                                      onInlineSave={onInlineSave}
                                                      onInlineCancel={onInlineCancel}
                                                      onDealTaskContactPersonSearch={onDealTaskContactPersonSearch}
                                                      onDealTaskContactPersonSearchClose={(
                                                        onDealTaskContactPersonSearchClose
                                                      )}
                                                    />
                                                  );
                                                })
                                              }
                                            </Fragment>
                                          ))
                                      }
                                      {
                                        (!isEditMode && !isNewDeal) && (!dealTaskGroup.tasks
                                          || !dealTaskGroup.tasks.length || !dealTaskCollapsedParents) && (
                                          <TableRowNoContent>
                                            <TableCell isText isSticky isStickyLeft isStickyGroupLeft>
                                              No tasks in this group
                                            </TableCell>
                                            <TableCell colSpan={visibleTableFieldsTotal - 1} />
                                          </TableRowNoContent>
                                        )
                                      }
                                      {
                                        (isEditMode || isNewDeal) && dealTaskNewItems[dealTaskGroup.id]
                                        && !!dealTaskNewItems[dealTaskGroup.id].length
                                        && dealTaskNewItems[dealTaskGroup.id].map((
                                          dealTask: DealTask,
                                        ): ReactElement => {
                                          const commonDealTaskIndex: number = ++commonRowIndex;

                                          return (
                                            <DealTaskRowForm
                                              isNewEntity
                                              withSorting
                                              isActive={!isNewDeal}
                                              isSaving={isSaving}
                                              isSaveDisabled={isSaveDisabled}
                                              isInlineSaving={(
                                                dealTaskInlineSaveId !== null && dealTaskGroupInlineSaveId !== null
                                                  ? dealTask.id === dealTaskInlineSaveId
                                                  && dealTaskGroup.id === dealTaskGroupInlineSaveId : isSaving
                                              )}
                                              key={dealTask.id}
                                              inputRefs={inputRefs}
                                              dealTaskIndex={commonDealTaskIndex}
                                              dealTaskData={dealTask}
                                              dealTaskGroupData={dealTaskGroup}
                                              dealTaskNewItems={dealTaskNewItems}
                                              dealTaskStatusList={dealTaskStatusList}
                                              dealTaskList={dealTaskGroup.tasks}
                                              dealTaskContactPersonList={dealTaskContactPersonList}
                                              accessLevelList={accessLevelList}
                                              onInlineSave={onInlineSave}
                                              onInlineCancel={onInlineCancel}
                                              onDeleteClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                deleteClick(event, dealTask.id, dealTaskGroup.id);
                                              }}
                                              onDealTaskContactPersonSearch={onDealTaskContactPersonSearch}
                                              onDealTaskContactPersonSearchClose={onDealTaskContactPersonSearchClose}
                                            />
                                          );
                                        })
                                      }
                                      {
                                        (isEditMode || isNewDeal) && (
                                          <CheckAccess permissions={dealTaskCreatePermissions}>
                                            <TableRowAddNewItem
                                              isSticky
                                              label="Add New Task"
                                              colSpan={visibleTableFieldsTotal}
                                              onClick={(): void => {
                                                addNewItem(dealTaskGroup.id);
                                              }}
                                            />
                                          </CheckAccess>
                                        )
                                      }
                                    </TableBody>
                                  </Table>
                                </HiddenBlock>
                              ) : (
                                <List>
                                  {
                                    dealTaskCollapsedParents && dealTaskGroup.tasks
                                    && dealTaskGroup.tasks.map((dealTask: DealTask): ReactElement => (
                                      <Fragment key={dealTask.id}>
                                        {
                                          [
                                            dealTask,
                                            ...(
                                              dealTask.subTasks && !!dealTask.subTasks.length ? dealTask.subTasks : []
                                            ),
                                          ].map((dealTaskItem: DealTask): ReactElement | boolean => {
                                            const isSelected: boolean = selectedRows ? withAllRowsSelected
                                              || checkIsRowSelected(dealTaskGroup.id, dealTaskItem.id, selectedRows)
                                              : false;
                                            const isSubTask: boolean = dealTaskItem.id !== dealTask.id;

                                            return ((isSubTask && dealTaskItem.parentId
                                              && dealTaskCollapsedParents[dealTaskItem.parentId]) || !isSubTask) && (
                                              <LongClickListItem
                                                isButton={!isSelectMode && !isExportMode}
                                                isActive={isSelected}
                                                isCompleted={dealTaskItem.status === DEAL_TASK_STATUS_COMPLETED}
                                                key={dealTaskItem.id}
                                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                  onRowClick(event, dealTaskItem, dealTaskGroup, true);
                                                }}
                                                onLongClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                  longClick(event, dealTaskGroup.id, dealTaskItem.id);
                                                }}
                                              >
                                                {
                                                  (isExportMode || isSelectMode) && (
                                                    <ListItemCheckbox isChecked={isSelected} itemId={dealTaskItem.id} />
                                                  )
                                                }
                                                <ActionButtonParent
                                                  isParent={!isEditMode && !isNewDeal && !isSubTask
                                                    && (dealTaskCollapsedParents[dealTaskItem.id]
                                                      || dealTaskCollapsedParents[dealTaskItem.id] === false)
                                                    && dealTaskItem.subTasks && !!dealTaskItem.subTasks.length}
                                                  isOpen={!!dealTaskCollapsedParents[dealTaskItem.id]}
                                                  withOffset={isSubTask}
                                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                    switchParentRow(event, dealTaskItem.id);
                                                  }}
                                                />
                                                {
                                                  !isEditMode && !isNewDeal && (
                                                    <DealTaskStatusIcon
                                                      withRightOffset
                                                      withLeftOffset={isSubTask}
                                                      dealTaskData={dealTaskItem}
                                                    />
                                                  )
                                                }
                                                <ListItemText isBlock>
                                                  {dealTaskItem.name}
                                                </ListItemText>
                                                <ListItemText isBlock isBlockRight>
                                                  {
                                                    !isEditMode && !isNewDeal ? dealTaskItem.statusText : (
                                                      <Tooltip title="Open in modal">
                                                        <ActionButton
                                                          withSmallPadding
                                                          onClick={(
                                                            event: React.MouseEvent<Element, MouseEvent>,
                                                          ): void => {
                                                            onRowClick(event, dealTaskItem, dealTaskGroup, true);
                                                          }}
                                                        >
                                                          <ArrowLinkIcon />
                                                        </ActionButton>
                                                      </Tooltip>
                                                    )
                                                  }
                                                </ListItemText>
                                              </LongClickListItem>
                                            );
                                          })
                                        }
                                      </Fragment>
                                    ))
                                  }
                                  {
                                    (!isEditMode && !isNewDeal) && (!dealTaskGroup.tasks || !dealTaskGroup.tasks.length
                                      || !dealTaskCollapsedParents) && (
                                      <NoContentBlock>
                                        No tasks in this group
                                      </NoContentBlock>
                                    )
                                  }
                                  {
                                    (isEditMode || isNewDeal) && dealTaskNewItems[dealTaskGroup.id]
                                    && dealTaskNewItems[dealTaskGroup.id].map((dealTask: DealTask): ReactElement => (
                                      <ListItem
                                        isButton
                                        key={dealTask.id}
                                        isActive={!isNewDeal}
                                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                          onRowClick(event, dealTask, dealTaskGroup, true);
                                        }}
                                      >
                                        <ActionButtonParent />
                                        <ListItemText isBlock>
                                          New Task
                                        </ListItemText>
                                        <ListItemText isBlock isBlockRight>
                                          <Tooltip title="Delete">
                                            <ActionButton
                                              withSmallPadding
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                deleteClick(event, dealTask.id, dealTaskGroup.id);
                                              }}
                                            >
                                              <RemoveIcon />
                                            </ActionButton>
                                          </Tooltip>
                                          <Tooltip title="Open in modal">
                                            <ActionButton
                                              withSmallPadding
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                onRowClick(event, dealTask, dealTaskGroup, true);
                                              }}
                                            >
                                              <ArrowLinkIcon />
                                            </ActionButton>
                                          </Tooltip>
                                        </ListItemText>
                                      </ListItem>
                                    ))
                                  }
                                  {
                                    (isEditMode || isNewDeal) && (
                                      <CheckAccess permissions={dealTaskCreatePermissions}>
                                        <Card
                                          isContainer
                                          isSelectable
                                          withSmallPadding
                                          onClick={(): void => {
                                            addNewItem(dealTaskGroup.id);
                                          }}
                                        >
                                          <ActionButtonNewItem withNoHighlight label="Add New Task" />
                                        </Card>
                                      </CheckAccess>
                                    )
                                  }
                                </List>
                              )
                            }
                          </CheckAccess>
                        </ExpansionGroup>
                      </DraggableItem>
                    );
                  })
                }
              </div>
              {
                isLoading && (
                  <Loader isInline />
                )
              }
            </ContentListContainerOffset>
          </Card>
        ) : (
          <NoContentBlock>
            No tasks found by your request
          </NoContentBlock>
        )
      }
      <ContextMenu
        menuAnchor={groupMenuAnchor}
        actionMenuItems={actionGroupMenuItems}
        dataAttributeEntityId={DATA_DEAL_TASK_GROUP_ID}
        onClose={closeGroupActionMenu}
      />
      <ContextMenu
        menuAnchor={menuAnchor}
        actionMenuItems={actionMenuItems}
        dataAttributeEntityId={DATA_DEAL_TASK_ID}
        onClose={closeActionMenu}
      />
      <ContextMenu
        menuAnchor={fileMenuAnchor}
        actionMenuItems={fileActionMenuItems}
        dataAttributeEntityId={DATA_ATTACHMENT_ID}
        dataAttributeEntityType={DATA_IS_ANALYZABLE}
        onClose={closeFileActionMenu}
        onCheckIsMenuItemVisible={onCheckIsFileMenuItemVisible}
      />
    </CheckAccess>
  );
});

export default DealTasks;
